import React from 'react'
import { Theme, withStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogActions from '@material-ui/core/DialogActions'
import { Grid, makeStyles } from '@material-ui/core'
import { isMobile } from 'react-device-detect'
import close from '../assets/images/close.svg'

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent)

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions)

interface ChartTooltipModalProps {
  open?: any
  setOpen?: any
  handleClose?: any
}
const useStyles = makeStyles({
  content: {
    background: '#FFFFFF',
    boxSizing: 'border-box',
    borderRadius: 3,
    padding: '24 24',
  },
  title: {
    color: '#3393F3',
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  subTitle: {
    color: '#3393F3',
    fontSize: 12,
    fontWeight: 400,
  },
  detailContent: {
    marginBottom: 12,
  },
  header: {
    marginBottom: 12,
    '& input': {
      float: 'right',
    },
  },
  typeHead: {
    fontSize: 14,
    color: 'rgba(24, 24, 24, 1)',
    fontWeight: 600,
  },
  typeDetail: {
    fontWeight: 400,
    fontSize: 14,
  },
})

const FundamentalTooltipModal = ({
  open,
  setOpen,
  handleClose,
}: ChartTooltipModalProps) => {
  const classes = useStyles()
  return (
    <div>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogContent>
          <div className={classes.content}>
            {!isMobile && (
              <div className={classes.header}>
                <span className={classes.title}>Fundamental Analysis </span>
                <span className={classes.subTitle}>
                  (Street Perception & Investor Confidence Measures)
                </span>
                <input
                  type="image"
                  onClick={() => handleClose()}
                  src={close}
                  alt="closeBtn"
                />
              </div>
            )}

            {isMobile && (
              <>
                <div className={classes.header} style={{ marginBottom: 5 }}>
                  <span className={classes.title}>Fundamental Analysis </span>
                  <input
                    type="image"
                    onClick={() => handleClose()}
                    src={close}
                    alt="closeBtn"
                  />
                </div>
                <div className={classes.subTitle} style={{ marginBottom: 10 }}>
                  (Street Perception & Investor Confidence Measures)
                </div>
              </>
            )}

            <div className={classes.detailContent}>
              <span className={classes.typeHead}>Fundamental Analysis: </span>
              <span className={classes.typeDetail}>
                The grid below is designed to provide a quick and efficient
                ‘scan’ of the major financial elements of a company –
                specifically: funding and capital structure; assets; outputs
                (e.g. sales, profits, cashflows) and market valuation. Viewed
                left to right in columns, it shows the normal flow of a company
                which is using funding to build assets, and then generating
                outputs from those assets. The quality and consistency of these
                outputs then impact the cost and availability of funding through
                the mechanism of market feedback. All types of companies are not
                the same, you will see different “scans” for different sectors,
                Financials (Banks) and Property will be a different set of
                compositions and ratios to non-financial companies.
              </span>
            </div>

            <div className={classes.detailContent}>
              <span className={classes.typeHead}>Composition Charts: </span>
              <span className={classes.typeDetail}>
                Each column contains a composition chart at the top, which shows
                the composition of that element of the company over time. This
                can be viewed either in absolute values, or in % composition,
                and allows you to quickly see abnormalities, dislocations and
                changes.
              </span>
            </div>

            <div className={classes.detailContent}>
              <span className={classes.typeHead}>Line Charts:</span>
              <span className={classes.typeDetail}>
                Underneath each composition chart are two indicators which are
                proxy ratios for broad financial health in that part of the
                company (e.g. asset liquidity and asset stability). These line
                charts express the company’s latest values in standard
                deviations i.e. ‘how far away from normal are they?’ – both
                against the company history for that company, and against a
                country/sector peer group where available. To understand what
                ‘normal’ looks like requires a certain amount of datapoints,
                younger companies are benchmarked against their peer group only.
                Each chart can be maximised to a bigger view, and also contains
                a specific ‘I’ text explaining what ratios are being used, why,
                and how to interpret them.
              </span>
            </div>

            <div className={classes.detailContent}>
              <span className={classes.typeHead}>Using the Scan:</span>
              <span className={classes.typeDetail}>
                To use this grid, simply scan it and look for abnormalities in
                the composition charts and/or extreme highs or lows in the line
                charts. Read the ‘I’ text on any abnormal charts for tips on how
                to interpret it, and on what to do next. Also bear in mind that
                this is a high-level overview. There may be specific, granular
                ratios we are looking at – for example in our forensic
                accounting analysis. Where there is a specific financial health
                issue, such as asset disposal losses or accruals, we will state
                this in your risk story feed. This has been built to be dynamic,
                only charts which are believed to offer useful insights are
                displayed, the composition and line charts view will adapt to
                sector, information value and data quality. On the line charts,
                if the company does not have enough fundamentals history to
                determine it’s “norm” we will display only the peer group
                analysis.
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(FundamentalTooltipModal)
